/**
 * Actions for the [target_access_permissions store]{link module:store/target_access_permissions}
 *
 * @module store/target_access_permissions/actions
 */

import * as apiTargetAccessPermissions from '@/api/target_access_permissions'
import { parseDeviceMetadata, parseSensorMetadata } from '@/store/modules/measurements/actions'
import { devicesAndSensorsToRelations } from '@/store/modules/measurements/relations'
import { DEFAULT_SENSOR_REGEXP, TargetAccessPermission, TimeRange } from '@/store/modules/target_access_permissions'

export function addTimeRangeToPermissions({ state }, { beginT, ctxt, emsId, endT, permissionIds }) {
  if (!permissionIds.length) return

  const newTimeRange = new TimeRange({ begin: beginT, end: endT })

  const resources = permissionIds
    .map((id) => {
      const permission = state.permissions[id]
      if (!permission || permission.targetType === 'devices') return

      let timeRanges = permission.timeRanges
      if (!timeRanges.length || (timeRanges.length === 1 && timeRanges[0].isOpen())) {
        timeRanges = [newTimeRange.toResourceObject()]
      } else {
        timeRanges = timeRanges.map((tR) => tR.toResourceObject())
        timeRanges.push(newTimeRange.toResourceObject())
      }

      return buildTargetAccessPermissionResource({ id, timeRanges })
    })
    .filter((x) => x !== undefined)

  const payload = buildAtomicOperationPayload({ resources, op: 'update' })
  return apiTargetAccessPermissions.updateTargetAccessPermissions({ ctxt, emsId, payload })
}

export function createDevicePermissionsWithDefaultSensors({ rootState }, { ctxt, deviceIds, emsId, userId }) {
  let allDeviceIds = []
  let allDefaultSensorIds = []
  let allDefaultStatusSensorIds = []

  for (const deviceId of deviceIds) {
    const deviceRelation = rootState.measurements.deviceRelations[deviceId]
    if (!deviceRelation) continue

    allDeviceIds = allDeviceIds.concat(deviceRelation.deviceIds())
    allDefaultSensorIds = allDefaultSensorIds.concat(
      deviceRelation.sensorIds({ sensorTypeRegExp: DEFAULT_SENSOR_REGEXP, type: 'sensors' })
    )
    allDefaultStatusSensorIds = allDefaultStatusSensorIds.concat(
      deviceRelation.sensorIds({ sensorTypeRegExp: DEFAULT_SENSOR_REGEXP, type: 'status_sensors' })
    )
  }
  const resources = buildCreateResources({
    deviceIds: allDeviceIds,
    sensorIds: allDefaultSensorIds,
    statusSensorIds: allDefaultStatusSensorIds,
    userId
  })
  const payload = buildAtomicOperationPayload({ resources, op: 'add' })
  return apiTargetAccessPermissions.updateTargetAccessPermissions({ ctxt, emsId, payload })
}

export function createSensorPermissions({ state, rootState }, { ctxt, emsId, sensorIds = [], userId }) {
  if (!sensorIds.length) return

  let allSensorIds = []
  let allStatusSensorIds = []
  const deviceRelations = Object.values(rootState.measurements.deviceRelations).filter((dR) =>
    dR.sensorIds().some((sId) => sensorIds.includes(sId))
  )
  const sensorRelations = deviceRelations.reduce(
    (prev, dR) => ({
      ...prev,
      ...dR.sensorRelations.reduce((prevSR, sR) => ({ ...prevSR, [parseInt(sR.id)]: sR }), {})
    }),
    {}
  )
  for (const sensorId of sensorIds) {
    const sensorRelation = sensorRelations[sensorId]
    if (!sensorRelation) continue

    if (sensorRelation.hasSensorWithType({ type: 'sensors' }))
      allSensorIds = [...allSensorIds, ...sensorRelation.sensorIds()]

    if (sensorRelation.hasSensorWithType({ type: 'status_sensors' }))
      allStatusSensorIds = [...allStatusSensorIds, ...sensorRelation.sensorIds()]
  }
  const allDeviceIds = deviceRelations.reduce((prev, dR) => [...prev, ...dR.deviceIds()], [])
  const deviceIdsWithPermission = Object.values(state.permissions).reduce(
    (prev, permission) => (permission.targetType === 'devices' ? [...prev, permission.targetId] : prev),
    []
  )

  const resources = buildCreateResources({
    deviceIds: allDeviceIds.filter((id) => !deviceIdsWithPermission.includes(id)),
    sensorIds: allSensorIds,
    statusSensorIds: allStatusSensorIds,
    userId
  })
  const payload = buildAtomicOperationPayload({ resources, op: 'add' })
  return apiTargetAccessPermissions.updateTargetAccessPermissions({ ctxt, emsId, payload })
}

export function deleteAllTimeRangesFromPermissions({ state }, { ctxt = 'customer', emsId, permissionIds = [] }) {
  const existingPermissionIds = permissionIds.filter((id) => state.permissions[id])

  const resources = existingPermissionIds.map((id) =>
    buildTargetAccessPermissionResource({ id, timeRanges: [{ begin: null, end: null }] })
  )
  const payload = buildAtomicOperationPayload({ resources, op: 'update' })

  return apiTargetAccessPermissions.updateTargetAccessPermissions({ ctxt, emsId, payload })
}

export function deletePermissions({ state }, { ctxt = 'customer', emsId, permissionIds = [] }) {
  if (!permissionIds.length) return

  const resources = permissionIds
    .map((id) => {
      if (!state.permissions[id]) return

      return buildTargetAccessPermissionResource({ id, withPermission: false })
    })
    .filter((x) => x !== undefined)

  const payload = buildAtomicOperationPayload({ resources, op: 'remove' })
  return apiTargetAccessPermissions.updateTargetAccessPermissions({ ctxt, emsId, payload })
}

export function deleteTimeRangesFromPermission(
  { commit, state },
  { ctxt, emsId, permissionId, deleteTimeRangeIndexes = [] }
) {
  const permission = state.permissions[permissionId]
  if (!permission || !deleteTimeRangeIndexes.length) return

  const timeRanges = permission.timeRanges.map((tR) => tR.toResourceObject())

  const remainingTimeRangeIndexes = Object.keys(timeRanges)
    .map((i) => parseInt(i))
    .filter((i) => !deleteTimeRangeIndexes.includes(i))

  const remainingTimeRanges = remainingTimeRangeIndexes.length
    ? timeRanges.reduce((prev, tR, i) => (remainingTimeRangeIndexes.includes(i) ? [...prev, tR] : prev), [])
    : [{ begin: null, end: null }]

  const resource = buildTargetAccessPermissionResource({ id: permissionId, timeRanges: remainingTimeRanges })
  const payload = buildAtomicOperationPayload({ resources: [resource], op: 'update' })
  return apiTargetAccessPermissions.updateTargetAccessPermissions({ ctxt, emsId, payload }).then(() => {
    commit('DELETE_TIME_RANGES', { deleteTimeRangeIndexes, permissionId })
  })
}

export function denyEverythingForUser(_, { ctxt, emsId, userId }) {
  return apiTargetAccessPermissions.purgeTargetAccessPermissions({
    ctxt,
    emsId,
    payload: {
      data: {
        type: 'deny_all_target_access_permissions',
        relationships: {
          user: buildUserResource({ userId })
        }
      }
    }
  })
}

export function duplicatePermissionsToUsers({ state }, { ctxt, emsId, permissionIds = [], userIds = [] }) {
  if (!permissionIds || !userIds.length) return
  const permissions = state.permissions

  const resources = permissionIds
    .map((permissionId) => {
      const permission = permissions[permissionId]
      if (!permission) return
      return buildTargetAccessPermissionResource({
        targetId: permission.targetId,
        targetType: permission.targetType,
        timeRanges: permission.timeRanges.map((tR) => ({
          begin: tR.begin?.toISOString(),
          end: tR.end?.toISOString()
        }))
      })
    })
    .filter((x) => x !== undefined)

  const resourcesWithUsers = userIds.reduce(
    (prev, userId) => [
      ...prev,
      ...resources.map((resource) => _.merge({}, resource, { relationships: { user: buildUserResource({ userId }) } }))
    ],
    []
  )
  const payload = buildAtomicOperationPayload({ resources: resourcesWithUsers, op: 'add' })
  return apiTargetAccessPermissions.updateTargetAccessPermissions({ ctxt, emsId, payload })
}

export function getPermissionTargets({ commit }, { ctxt, emsId, locale }) {
  return apiTargetAccessPermissions.getPermissionTargets({ ctxt, emsId, locale }).then((rsp) => {
    const data = rsp.payload.data
    const included = rsp.payload.included

    const deviceResources = data.filter((item) => ['devices'].includes(item.type))
    const sensorResources = data.filter((item) => ['sensors', 'status_sensors'].includes(item.type))

    const devices = parseDeviceMetadata({ included, data: deviceResources })
    const sensors = parseSensorMetadata({ included, data: sensorResources })
    const deviceRelations = devicesAndSensorsToRelations({
      devices: Object.values(devices),
      sensors: Object.values(sensors)
    })

    commit('measurements/SET_DEVICES', devices, { root: true })
    commit('measurements/SET_SENSORS', { prepareVals: false, sensors }, { root: true })
    commit('measurements/SET_DEVICE_RELATIONS', { deviceRelations }, { root: true })

    return data
  })
}
export function getTargetAccessPermissions({ commit }, { ctxt, emsId, userIds }) {
  const apiParams = { ctxt, emsId }
  if (userIds) apiParams.userIds = userIds

  commit('RESET_STORE')

  return apiTargetAccessPermissions.getTargetAccessPermissions(apiParams).then((rsp) => {
    const meta = rsp.payload.meta
    commit('SET_FULL_PERMISSION_USER_IDS', {
      fullPermissionUserIds: meta?.fullPermissionUserIds?.map((id) => parseInt(id))
    })
    commit('SET_RESTRICTED_PERMISSION_USER_IDS', {
      restrictedPermissionUserIds: meta?.restrictedPermissionUserIds?.map((id) => parseInt(id))
    })

    let data = rsp.payload.data
    // parse list or single TAP
    if (!(data instanceof Array)) data = [data]

    for (const targetAccessPermissionResource of data) {
      const targetAccessPermission = parseTargetAccessPermissionResource(targetAccessPermissionResource)
      commit('SET_TARGET_ACCESS_PERMISSION', { targetAccessPermission })
    }

    return data
  })
}

export function permitEverythingForUser(_, { ctxt, emsId, userId }) {
  return apiTargetAccessPermissions.purgeTargetAccessPermissions({
    ctxt,
    emsId,
    payload: {
      data: {
        type: 'permit_all_target_access_permissions',
        relationships: {
          user: buildUserResource({ userId })
        }
      }
    }
  })
}

const buildUserResource = ({ userId }) => ({
  data: {
    type: 'users',
    id: userId ? userId.toString() : null
  }
})

const buildTargetAccessPermissionResource = ({
  id,
  targetId,
  targetType,
  timeRanges,
  userId,
  withPermission = true
}) => {
  const resource = {
    type: 'target_access_permissions',
    id: id ? id.toString() : null
  }

  if (targetId && targetType) {
    resource.relationships ||= {}
    resource.relationships.permissionTarget = {
      data: {
        type: targetType,
        id: targetId ? targetId.toString() : null
      }
    }
  }
  if (timeRanges) {
    resource.attributes ||= {}
    resource.attributes.timeRanges = timeRanges
  }
  if (userId) {
    resource.relationships ||= {}
    resource.relationships.user = buildUserResource({ userId })
  }
  if (withPermission) {
    if (!resource.attributes) resource.attributes = {}
    resource.attributes.permission = 1
  }

  return resource
}

const parseTargetAccessPermissionResource = (resource) => {
  const target = resource?.relationships?.permissionTarget?.data
  let timeRanges = null
  if (resource?.attributes?.timeRanges) {
    timeRanges = resource?.attributes?.timeRanges?.map((tr) => new TimeRange(tr))
  } else {
    timeRanges = [new TimeRange({})]
  }
  return new TargetAccessPermission({
    id: resource.id,
    permission: parseInt(resource?.attributes?.permission),
    targetId: parseInt(target?.id),
    targetType: target?.type,
    timeRanges,
    userId: parseInt(resource?.relationships?.user?.data?.id)
  })
}

const buildCreateResources = ({ deviceIds = [], sensorIds = [], statusSensorIds = [], userId }) => [
  ...deviceIds.reduce(
    (prev, deviceId) => [
      ...prev,
      buildTargetAccessPermissionResource({ targetId: deviceId, targetType: 'devices', userId })
    ],
    []
  ),
  ...sensorIds.reduce(
    (prev, sensorId) => [
      ...prev,
      buildTargetAccessPermissionResource({ targetId: sensorId, targetType: 'sensors', userId })
    ],
    []
  ),
  ...statusSensorIds.reduce(
    (prev, sensorId) => [
      ...prev,
      buildTargetAccessPermissionResource({ targetId: sensorId, targetType: 'status_sensors', userId })
    ],
    []
  )
]

const buildAtomicOperationPayload = ({ resources, op }) => ({
  'atomic:operations': resources.reduce((prev, data) => [...prev, { data, op }], [])
})
